// ContestDuplicateModal
//
// Duplicates an existing contest. Lives on the contest detail page
// (admin/manager-only); opens when the user taps "Duplicate" in the
// top action bar next to Edit. Two modes:
//
//   * "Run again" (rerun) — one new contest at the SAME locations with
//     a new date range, for the "same contest month over month"
//     cadence. Preserves the source's type/scope, so store-vs-store
//     contests are supported here (and ONLY here). Per-store targets
//     copy as-is; the admin edits the new contest if a goal changed.
//   * "Copy to locations" (rollout) — the original behavior: an
//     independent single-store contest per selected target store, with
//     a template-driven per-store name. SVS sources are rejected by
//     the backend in this mode, so the toggle is hidden for them.
//
// In both modes participants are NOT copied — empty means "everyone at
// the contest's locations," which is the right roster for a new period
// or a new store (new hires included automatically).
//
// The form is intentionally narrower than ContestFormBuilder — only
// the fields that change between duplicates are exposed. Everything
// else carries over from the source.

import React, { useEffect, useMemo, useState } from "react";
import { Modal, Box } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_duplicate_contest,
  API_get_eligible_stores,
} from "../../../constants";
import { DateField } from "./ContestFormBuilder";

const DEFAULT_TEMPLATE = "{name} — {store}";

// Pure helper — kept module-scoped so the preview row and the submit
// path both produce the SAME rendered name. `.replace` rather than
// `.format` to mirror the backend's safe substitution (no reflection
// on attributes from user-typed template input).
function renderName(template, sourceName, storeName) {
  if (!template || !template.trim()) return sourceName;
  return template.replace("{name}", sourceName).replace("{store}", storeName);
}

// Default "run again" range: the period right after the source's.
// Month-over-month is the motivating cadence, so a source that spans
// exactly one calendar month advances to the NEXT calendar month
// (June 1–30 → July 1–31, not July 1–30); any other span slides
// forward by its own length (a 2-week sprint → the next 2 weeks).
function nextPeriod(startISO, endISO) {
  const start = dayjs(startISO);
  const end = dayjs(endISO);
  if (!start.isValid() || !end.isValid()) return { start: "", end: "" };
  const isCalendarMonth =
    start.date() === 1 && end.isSame(start.endOf("month"), "day");
  if (isCalendarMonth) {
    const nextStart = start.add(1, "month");
    return {
      start: nextStart.format("YYYY-MM-DD"),
      end: nextStart.endOf("month").format("YYYY-MM-DD"),
    };
  }
  const lengthDays = end.diff(start, "day");
  const nextStart = end.add(1, "day");
  return {
    start: nextStart.format("YYYY-MM-DD"),
    end: nextStart.add(lengthDays, "day").format("YYYY-MM-DD"),
  };
}

// `height: 45` matches the contest form's `LocationsMultiSelect`
// trigger — keeps the two surfaces visually consistent so the
// duplicate sheet doesn't read as a different control just because
// MUI's default Select height is taller.
const SELECT_SX = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: 45,
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

const CHECKBOX_SX = {
  color: "#6d28d9",
  "&.Mui-checked": { color: "#6d28d9" },
};

export default function ContestDuplicateModal({
  isOpen,
  onClose,
  contest,
  // storeNames is kept as a fallback for the loading window / fetch
  // failure, mirroring what ContestFormBuilder does.
  storeNames,
  homeStore,
}) {
  // Store-vs-store sources only support rerun mode — the backend
  // rejects them in rollout mode (each rollout clone is inherently
  // single-store, contradicting SVS's ≥2-locations rule) — so the
  // mode toggle is hidden and the rollout-only stores fetch skipped.
  const isStoreContest = contest?.contest_type === "store_vs_store";

  // "rerun" = same locations, new dates (one contest);
  // "rollout" = the original clone-to-other-stores flow.
  const [mode, setMode] = useState("rerun");
  const [rerunName, setRerunName] = useState("");
  const [rerunStart, setRerunStart] = useState("");
  const [rerunEnd, setRerunEnd] = useState("");

  // Eligible target stores (rollout mode only). We fetch the
  // contest-info app's eligible-stores endpoint on open instead of
  // using the `storeNames` prop because that prop is the user's
  // CROSS-COMPANY membership list and would leak stores from other
  // companies into the duplicate sheet. Same filter the contest
  // creation form uses — single source of truth for "which stores can
  // this user create contests at." Falls back to `storeNames` only on
  // fetch failure.
  const [companyStores, setCompanyStores] = useState(null);
  useEffect(() => {
    if (!isOpen || isStoreContest) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_eligible_stores, {
          store_id: homeStore,
        });
        if (cancelled) return;
        setCompanyStores(res.data?.redux?.payload?.stores || []);
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Failed to load eligible stores for duplicate", err);
          setCompanyStores(null);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, homeStore]);

  // Source stores are excluded — cloning to a store the source
  // already covers would produce two contests at the same store with
  // the same metric/dates, which is rarely intentional.
  const sourceStoreIds = useMemo(
    () => new Set((contest?.store_ids || []).map(Number)),
    [contest]
  );

  // Distinguish three states (same trick as ContestFormBuilder's
  // locationsSource):
  //   * `companyStores === null` → fetch in flight OR failed. Fall
  //     back to the membership-only `storeNames` prop so the modal
  //     is still usable.
  //   * `companyStores === [...]` → use the fetch result, even when
  //     empty (no fallback to the cross-company prop).
  const eligibleStores = useMemo(() => {
    const source =
      Array.isArray(companyStores) ? companyStores : storeNames || [];
    return source
      .filter((s) => !sourceStoreIds.has(Number(s.id)))
      .map((s) => ({ id: Number(s.id), store_name: s.store_name }));
  }, [companyStores, storeNames, sourceStoreIds]);

  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal reopens — without this, closing
  // and reopening would carry forward the previous selection set. The
  // rerun fields re-seed from the source: same name, next period.
  useEffect(() => {
    if (isOpen) {
      setSelectedStoreIds([]);
      setTemplate(DEFAULT_TEMPLATE);
      setSubmitting(false);
      setMode("rerun");
      setRerunName(contest?.name || "");
      const { start, end } = nextPeriod(contest?.start_date, contest?.end_date);
      setRerunStart(start);
      setRerunEnd(end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleStoresChange = (event) => {
    const { value } = event.target;
    const arr = Array.isArray(value) ? value : [value];
    if (arr.includes("__all__")) {
      // Sentinel: toggle select-all / deselect-all
      setSelectedStoreIds((prev) =>
        prev.length === eligibleStores.length
          ? []
          : eligibleStores.map((s) => s.id)
      );
      return;
    }
    setSelectedStoreIds(arr.map(Number));
  };

  // Preview the first 3 resulting names so the admin can sanity-check
  // their template before submitting. Names are computed from the
  // currently-selected targets in selection order (not source order).
  const previewNames = useMemo(() => {
    const targets = eligibleStores
      .filter((s) => selectedStoreIds.includes(s.id))
      .slice(0, 3);
    return targets.map((s) => renderName(template, contest?.name || "", s.store_name));
  }, [eligibleStores, selectedStoreIds, template, contest?.name]);

  const handleRerunSubmit = async () => {
    if (!rerunStart || !rerunEnd) {
      toast.error("Pick a start and end date", { autoClose: 3000 });
      return;
    }
    if (dayjs(rerunEnd).isBefore(dayjs(rerunStart))) {
      toast.error("End date cannot be before start date", { autoClose: 3000 });
      return;
    }
    setSubmitting(true);
    try {
      const res = await postRightworkAPI(API_duplicate_contest, {
        source_contest_id: contest.id,
        store_id: homeStore,
        rerun: true,
        start_date: rerunStart,
        end_date: rerunEnd,
        name: (rerunName || "").trim() || contest.name,
      });
      if (res.data?.redux?.payload?.status === "OK") {
        toast.success("Contest created for the new dates", { autoClose: 2500 });
        onClose(true);
      } else {
        throw new Error(res.data?.system?.message || "Duplicate failed");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.system?.message ||
          e?.message ||
          "Couldn't duplicate the contest. Try again.",
        { autoClose: 3500 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedStoreIds.length === 0) {
      toast.error("Pick at least one location to duplicate to", {
        autoClose: 3000,
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await postRightworkAPI(API_duplicate_contest, {
        source_contest_id: contest.id,
        store_id: homeStore,
        target_store_ids: selectedStoreIds,
        name_suffix_template: template || DEFAULT_TEMPLATE,
      });
      if (res.data?.redux?.payload?.status === "OK") {
        const count = res.data.redux.payload.created_count || 0;
        toast.success(
          `Duplicated to ${count} ${count === 1 ? "location" : "locations"}`,
          { autoClose: 2500 }
        );
        onClose(true);
      } else {
        throw new Error(res.data?.system?.message || "Duplicate failed");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.system?.message ||
          e?.message ||
          "Couldn't duplicate the contest. Try again.",
        { autoClose: 3500 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!contest) return null;

  return (
    <Modal open={isOpen} onClose={() => !submitting && onClose(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 92vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "white",
          borderRadius: "16px",
          boxShadow: 24,
          p: 4,
        }}
      >
        {/* Title row */}
        <div className="flex items-center gap-2 mb-1">
          <ContentCopyIcon className="text-violet-700" style={{ fontSize: 22 }} />
          <h2 className="text-xl font-semibold text-slate-900">
            Duplicate contest
          </h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {mode === "rerun" ? (
            <>
              Runs <span className="font-semibold">{contest.name}</span> again
              at the same location{(contest.store_ids || []).length > 1 ? "s" : ""}{" "}
              with a new date range. Same metric, winners, rewards, and
              per-store targets; the participant roster resets to "everyone at
              the location."
            </>
          ) : (
            <>
              Creates an independent copy of{" "}
              <span className="font-semibold">{contest.name}</span> at each
              selected location. Same metric, winners, and rewards; the
              participant roster resets to "everyone at the location."
            </>
          )}
        </p>

        {/* Mode toggle. Hidden for store-vs-store sources — rollout mode
            is backend-rejected for them, so rerun is the only option. */}
        {!isStoreContest && (
          <div className="flex rounded-lg bg-slate-100 p-1 mb-5">
            {[
              { key: "rerun", label: "Run again", Icon: ReplayIcon },
              { key: "rollout", label: "Copy to other locations", Icon: ContentCopyIcon },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => !submitting && setMode(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
                  mode === key
                    ? "bg-white text-violet-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon style={{ fontSize: 15 }} />
                {label}
              </button>
            ))}
          </div>
        )}

        {mode === "rerun" ? (
          <>
            {/* Locations are fixed in rerun mode — the whole point is
                "same contest, same places, next period." Changing the
                store set is what rollout mode / the edit form are for. */}
            <div className="mb-5">
              <p className="text-sm font-medium text-slate-700 mb-1.5">
                Location{(contest.store_names || []).length > 1 ? "s" : ""}
              </p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-700">
                {(contest.store_names || []).join(", ")}
              </div>
            </div>

            {/* Name — prefilled with the source's name. Duplicate names
                across periods are fine (the list shows dates), but the
                admin can add a "July" style suffix here if they want. */}
            <div className="mb-5">
              <p className="text-sm font-medium text-slate-700 mb-1.5">Name</p>
              <TextField
                fullWidth
                size="small"
                value={rerunName}
                onChange={(e) => setRerunName(e.target.value)}
                placeholder={contest.name}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    fontSize: 14,
                    height: 45,
                    padding: "5px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            {/* New date range — pre-seeded to the period right after the
                source's (next calendar month when the source spans one). */}
            <div className="mb-2 flex gap-4 flex-row">
              <DateField
                label="Start date *"
                value={rerunStart}
                onChange={setRerunStart}
              />
              <DateField
                label="End date *"
                value={rerunEnd}
                onChange={setRerunEnd}
                minDate={rerunStart}
              />
            </div>
          </>
        ) : eligibleStores.length === 0 ? (
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-3 text-sm text-slate-600">
            This contest already covers every location you can access — no
            targets available to duplicate to.
          </div>
        ) : (
          <>
            {/* Target locations */}
            <div className="mb-5">
              <p className="text-sm font-medium text-slate-700 mb-1.5">
                Target locations <span className="text-rose-500">*</span>
                {selectedStoreIds.length > 0 && (
                  <span className="text-slate-400 font-normal ml-1">
                    ({selectedStoreIds.length})
                  </span>
                )}
              </p>
              <Select
                multiple
                value={selectedStoreIds.map(String)}
                onChange={handleStoresChange}
                input={<OutlinedInput />}
                sx={{ ...SELECT_SX, width: "100%" }}
                MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="text-slate-400">
                        Pick locations to duplicate to…
                      </span>
                    );
                  }
                  const ids = new Set(selected.map(Number));
                  const names = eligibleStores
                    .filter((s) => ids.has(s.id))
                    .map((s) => s.store_name);
                  if (names.length <= 2) return names.join(", ");
                  return `${names[0]}, ${names[1]}, +${names.length - 2} more`;
                }}
                displayEmpty
              >
                <MenuItem value="__all__">
                  <Checkbox
                    checked={selectedStoreIds.length === eligibleStores.length}
                    indeterminate={
                      selectedStoreIds.length > 0 &&
                      selectedStoreIds.length < eligibleStores.length
                    }
                    sx={CHECKBOX_SX}
                  />
                  <ListItemText
                    primary={
                      selectedStoreIds.length === eligibleStores.length
                        ? "Deselect all"
                        : "Select all"
                    }
                    primaryTypographyProps={{ style: { fontWeight: 600 } }}
                  />
                </MenuItem>
                {eligibleStores.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    <Checkbox
                      checked={selectedStoreIds.includes(s.id)}
                      sx={CHECKBOX_SX}
                    />
                    <ListItemText primary={s.store_name} />
                  </MenuItem>
                ))}
              </Select>
              {(contest.store_ids || []).length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Source location{(contest.store_ids || []).length > 1 ? "s are" : " is"}{" "}
                  excluded from this list — edit the original to change those.
                </p>
              )}
            </div>

            {/* Name template */}
            <div className="mb-2">
              <p className="text-sm font-medium text-slate-700 mb-1.5">
                Name template
              </p>
              <TextField
                fullWidth
                size="small"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                placeholder={DEFAULT_TEMPLATE}
                InputProps={{
                  // `height: 45` + `padding: 5px` mirrors the contest
                  // form's text-input pattern so this TextField sits at
                  // the same height as the Select above it.
                  sx: {
                    borderRadius: "8px",
                    fontSize: 14,
                    height: 45,
                    padding: "5px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
              <p className="text-xs text-slate-500 mt-1">
                Use{" "}
                <code className="bg-slate-100 rounded px-1 py-0.5 text-[11px]">
                  {"{name}"}
                </code>{" "}
                for the source contest's name and{" "}
                <code className="bg-slate-100 rounded px-1 py-0.5 text-[11px]">
                  {"{store}"}
                </code>{" "}
                for each target location.
              </p>
            </div>

            {/* Preview — first 3 of the would-be names. Helps the
                admin spot template typos before submit (e.g., they
                forgot the {store} placeholder and every duplicate
                would end up with the same name). */}
            {previewNames.length > 0 && (
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 mb-4 mt-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">
                  Preview
                </div>
                {previewNames.map((n, i) => (
                  <div key={i} className="text-xs text-slate-700 truncate">
                    {n || (
                      <span className="text-slate-400 italic">(empty name)</span>
                    )}
                  </div>
                ))}
                {selectedStoreIds.length > previewNames.length && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    +{selectedStoreIds.length - previewNames.length} more
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={() => onClose(false)}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={mode === "rerun" ? handleRerunSubmit : handleSubmit}
            disabled={
              submitting ||
              (mode === "rerun"
                ? !rerunStart || !rerunEnd
                : eligibleStores.length === 0 || selectedStoreIds.length === 0)
            }
            className="px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium disabled:opacity-60"
          >
            {submitting
              ? "Duplicating…"
              : mode === "rerun"
              ? "Run again"
              : selectedStoreIds.length > 1
              ? `Duplicate to ${selectedStoreIds.length} locations`
              : "Duplicate"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}
